'use client'

import Script from 'next/script'
import { useEffect } from 'react'

import { useEnvs } from '@marketplace-web/environment/environment-util'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { useSession } from '@marketplace-web/shared/session-data'
import { stringToSha256 } from '@marketplace-web/crypto/crypto-util'

import { logIncogniaWarning } from '../../utils/observability'

const SCRIPT_SOURCE = 'https://repo.incognia.com/web-sdk/v1'

const IncogniaScript = () => {
  const incogniaAppId = useEnvs('INCOGNIA_WEB_CLIENT_SIDE_KEY')
  const isIncogniaModuleEnabled = useFeatureSwitch('web_incognia_script')
  const { user } = useSession()

  const setupAccountId = async () => {
    if (!window.IncogniaWebSdk) return

    if (user) {
      const encryptedUserId = await stringToSha256(user.id.toString())

      window.IncogniaWebSdk.setAccountId(encryptedUserId)

      return
    }

    window.IncogniaWebSdk.clearAccountId()
  }

  const initializeIncognia = async () => {
    if (!window.IncogniaWebSdk) {
      logIncogniaWarning('IncogniaWebSdk is not defined')

      return
    }

    if (!incogniaAppId) return

    try {
      await window.IncogniaWebSdk.init({ appId: incogniaAppId })
      setupAccountId()
    } catch (error) {
      logIncogniaWarning('IncogniaWebSdk initialization failed', { error })
    }
  }

  useEffect(() => {
    if (!isIncogniaModuleEnabled || incogniaAppId) return

    logIncogniaWarning('Incognia web client side key is not set')
  }, [incogniaAppId, isIncogniaModuleEnabled])

  if (!isIncogniaModuleEnabled) return null
  if (!incogniaAppId) return null

  return (
    <Script
      id="incognia-script"
      src={SCRIPT_SOURCE}
      strategy="afterInteractive"
      onLoad={initializeIncognia}
      onError={error => logIncogniaWarning('Incognia script loading failed', { error })}
    />
  )
}

export default IncogniaScript
